import React, { useEffect, useState, useCallback } from 'react';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { getStatusColor } from '../utils/requestUtils';

const RequestsList = ({ requests, selectedRequest, setSelectedRequest }) => {
  const [toolImages, setToolImages] = useState({});

  const fetchToolImages = useCallback(async () => {
    const imagePromises = requests.map(async (request) => {
      if (request.toolId) {
        const toolDoc = await getDoc(doc(db, 'tools', request.toolId));
        if (toolDoc.exists()) {
          const toolData = toolDoc.data();
          return { [request.id]: toolData.imageUrls?.[0] || null };
        }
      }
      return { [request.id]: null };
    });

    const images = await Promise.all(imagePromises);
    const imagesObject = Object.assign({}, ...images);
    setToolImages(imagesObject);
  }, [requests]);

  useEffect(() => {
    fetchToolImages();
  }, [requests, fetchToolImages]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    
    if (isNaN(date.getTime())) {
      console.error('Invalid date:', dateString);
      return 'Invalid Date';
    }
    
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    });
  };

  const handleRequestClick = (request) => {
    setSelectedRequest(request);
  };

  if (requests.length === 0) {
    return (
      <div className="p-6 bg-white border rounded-lg shadow-sm flex items-center justify-center min-h-[200px]">
        <p className="text-gray-500">No requests found</p>
      </div>
    );
  }

  return (
    <div className="overflow-y-auto pr-2" style={{ maxHeight: 'calc(100vh - 200px)' }}>
      <ul className="space-y-3">
        {requests.map(request => (
          <li
            key={request.id}
            className={`bg-white rounded-lg border shadow-sm transition-all duration-200 cursor-pointer
              ${selectedRequest?.id === request.id 
                ? 'border-blue-300 ring-2 ring-blue-100' 
                : 'border-gray-200 hover:border-gray-300 hover:shadow'}`}
            onClick={() => handleRequestClick(request)}
          >
            <div className="p-4">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-20 h-20 border rounded-lg overflow-hidden">
                  {toolImages[request.id] ? (
                    <img 
                      src={toolImages[request.id]} 
                      alt={request.toolName} 
                      className="w-full h-full object-cover" 
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                      <span className="text-gray-400 text-xs">No image</span>
                    </div>
                  )}
                </div>
                
                <div className="flex-grow min-w-0">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-lg font-medium text-gray-900 truncate pr-2">
                      {request.toolName}
                    </h3>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(request.status)}`}>
                      {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-2">
                    {request.type === 'inbound' ? `Borrower: ${request.borrowerName}` : `Owner: ${request.ownerName}`}
                  </p>
                  
                  {request.requestedDates && request.requestedDates.length > 0 && (
                    <div className="flex items-center space-x-3 text-xs text-gray-500">
                      <span>
                        {formatDate(request.requestedDates[0].startDate)}
                      </span>
                      <span className="text-gray-400">→</span>
                      <span className="font-medium">
                        {formatDate(request.requestedDates[0].endDate)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default RequestsList;
